/** The platform's own words (chrome, modes, presets, errors). A domain pack adds its own on top. */
import type { Catalogs } from './translator';

/** The flag shown with each language's name (the language row of the main bar). */
export const LANGUAGE_FLAGS: Readonly<Record<string, string>> = { it: '🇮🇹', en: '🇬🇧', de: '🇩🇪', fr: '🇫🇷', es: '🇪🇸' };

export const PLATFORM_STRINGS: Catalogs = {
  it: {
    'ai.badge': 'Assistente AI',
    'chat.placeholder': 'Scrivi un messaggio…',
    'chat.send': 'Invia',
    'chat.timeout': 'Nessuna risposta dal server. Riprova.',
    'chat.send_failed': 'Messaggio non inviato: connessione assente.',
    'chat.turn_failed': 'Qualcosa è andato storto. Riprova.',
    'chat.vision_failed': 'Non sono riuscito a leggere il documento. Riprova o inserisci i dati a mano.',
    'chat.from_operator': 'Operatore',
    'chat.announcement': 'Avviso',
    'chat.not_sent': 'non inviato',
    'chat.empty': 'Parla con me o tocca lo schermo.',
    'chat.history': 'La conversazione fin qui',
    'chat.back_to_current': 'Torna al messaggio corrente',
    'voice.mic_on': 'Microfono acceso',
    'voice.mic_off': 'Accendi il microfono',
    'voice.stop': 'Interrompi',
    'voice.listening': 'Ti ascolto…',
    'voice.processing': 'Ci penso…',
    'voice.speaking': 'Sto parlando',
    'voice.interrupted': 'Interrotto',
    'voice.disabled_by_mode': 'Voce spenta in questa modalità',
    'voice.error': 'Microfono non disponibile',
    'action.restart': 'Ricomincia',
    'action.help': 'Aiuto',
    'action.help.words': 'Aiuto',
    'action.language': 'Lingua',
    'language.it': 'Italiano',
    'language.en': 'English',
    'language.de': 'Deutsch',
    'session.copy': 'Copia l\'id della sessione',
    'session.copied': 'Copiato',
    'session.title': 'Sessione',
    'session.new': 'Nuova sessione',
    'error.part_failed': 'Questa parte non si è caricata.',
    'error.details': 'Dettagli tecnici',
    'menu.back': 'Indietro',
    'menu.return': 'Torna',
    'language.fr': 'Français',
    'language.es': 'Español',
    'language.choose': 'Scegli la lingua · Choose your language',
    'status.online': 'In linea',
    'status.connecting': 'Connessione…',
    'status.retrying': 'Riconnessione…',
    'status.offline': 'Non in linea',
    'status.starting': 'Avvio…',
    'status.replay': 'Replay',
    'status.recovery': 'Se preferisci, tocca lo schermo.',
    'inactivity.title': 'Sei ancora lì?',
    'inactivity.here': 'Sono qui',
    'inactivity.seconds': '{n} s',
    'mode.title': 'Accessibilità',
    'mode.none': 'Standard',
    'mode.visual': 'Ipovisione',
    'mode.hearing': 'Udito',
    'mode.motor': 'Mobilità ridotta',
    'mode.cognitive': 'Semplice',
    'mode.local': 'solo su questo schermo',
    'preset.kiosk': 'Avatar',
    'preset.text': 'Testo',
    'preset.main_bar_top': 'Barra in alto',
    'preset.content_first': 'Contenuto in alto',
    'role.operator': 'Operatore collegato alla sessione {id}',
    'role.replay': 'Replay della sessione {id}',
    'role.replay_short': 'Replay {id}',
    'role.operator_short': 'Operatore · {id}',
    'role.replay_tag': 'Replay',
    'role.operator_tag': 'Operatore',
    'screen.unknown': 'La schermata "{name}" non ha ancora un aspetto in questa interfaccia.',
    'widget.unknown': 'Il widget "{name}" non esiste.',
    'widget.bad_props': 'Dati non validi per il widget "{name}".',
    'overlay.close': 'Chiudi',
    'keyboard.space': 'spazio',
    'keyboard.done': 'Fatto',
    'boot.connecting': 'Mi collego al totem…',
    'boot.no_domain': 'Il backend non dice che dominio usa, e nessun pack riconosce la sessione.',
    'boot.unknown_domain': 'Nessuna interfaccia per il dominio "{domain}".',
    'boot.replay_failed': 'Non riesco a leggere la sessione registrata {id}.',
    'boot.operator_needs_session': 'Per collegarti come operatore serve ?session=<id>.',
    'module.progress': 'Avanzamento',
    'module.chat': 'Conversazione',
    'module.composer': 'Scrittura e voce',
    'module.avatar_window': 'Finestra avatar',
    'module.content': 'Contenuto',
    'module.main_bar': 'Barra principale',
    'module.status': 'Barra di stato',
    'theme.classico': 'Classico',
    'theme.essenziale': 'Essenziale',
    'preset.essenziale': 'Essenziale',
    'settings.theme': 'Tema',
    'settings.tab.look': 'Aspetto',
    'settings.tab.parts': 'Parti',
    'settings.tab.screens': 'Schermate',
    'settings.screens.title': 'Il widget di ogni passo',
    'settings.screens.none': 'Questo dominio non ha schermate con più aspetti.',
    'settings.screens.end': 'Fine',
    'settings.details.title': 'Dettagli delle card',
    'settings.details.closes': 'Si chiudono da soli dopo',
    'settings.details.seconds': '{n} s',
    'settings.details.never': 'Mai',
    'settings.face': 'Assistente',
    'settings.background': 'Sfondo',
    'background.auto': 'Automatico',
    'background.none': 'Nessuno',
    'face.auto': 'Automatico',
    'face.musetalk': 'MuseTalk',
    'face.lowpoly': '3D',
    'face.idle': 'Idle',
    'face.particles': 'Particelle',
    'settings.scheme': 'Colori',
    'settings.scheme_light': 'Chiaro',
    'settings.scheme_dark': 'Scuro',
    'settings.modules': 'Aspetto delle parti',
    'settings.lyrics_history': 'Frasi precedenti visibili',
    'settings.karaoke': 'Evidenzia le parole mentre parla',
    'module.avatar': 'Avatar',
    'module.variant.standard': 'Standard',
    'module.variant.minimal_top': 'Minimale',
    'module.variant.lyrics': 'Lyrics',
    'module.variant.portrait': 'Ritratto',
    'module.variant.pills': 'Pillole',
    'module.variant.pill_composer': 'Pillola',
    'module.variant.minimal_footer': 'Minimale',
    'widget.variant.single': 'Uno alla volta',
    'widget.variant.card': 'Card',
    'widget.variant.classic': 'Variante 2 (classica)',
    'widget.variant.video': 'Video',
    'status.step_of': '{n} di {total} · {name}',
    'settings.title': 'Impostazioni',
    'settings.ui': 'Interfaccia',
    'settings.chat': 'Conversazione',
    'settings.chat_align': 'Disposizione dei messaggi',
    'settings.align_side': 'Ai lati',
    'settings.align_center': 'Al centro',
    'settings.chat_size': 'Dimensione del testo',
    'settings.chat_width': 'Larghezza dei messaggi',
    'settings.screen': 'Schermo',
    'settings.frame_padding': 'Margine dai bordi',
    'settings.panel_padding': 'Spazio dentro le schede',
    'settings.role_frame': 'Cornice replay e operatore',
    'settings.frame_solid': 'Piena',
    'settings.frame_fade': 'Sfumata',
    'settings.frame_off': 'Nessuna',
    'settings.none': 'Nessuno',
    'settings.widgets': 'Aspetto dei riquadri',
    'settings.auto': 'Automatico',
    'settings.options': 'Opzioni',
    'settings.touch_pills': 'Mostra i tocchi in chat',
    'settings.keyboard_button': 'Tasto della tastiera a schermo',
    'settings.keyboard_place': 'Dove si apre la tastiera',
    'settings.keyboard_place.bottom': 'In basso, grande',
    'settings.keyboard_place.box': 'Sopra la casella, piccola',
    'settings.motion': 'Animazioni',
    'settings.speak_typed': 'Parla anche col microfono spento',
    'settings.turn_details': 'Debug sotto i messaggi',
    'debug.results': 'a schermo',
    'debug.now_default': '(ora il default)',
    'debug.changed': 'Stato dopo il turno: {n} campi cambiati',
    'debug.legend.changed': 'verde',
    'debug.legend.changed_text': 'cambiato in questo turno',
    'debug.legend.earlier': 'giallo',
    'debug.legend.earlier_text': 'impostato nei turni precedenti',
    'debug.legend.previous': 'barrato',
    'debug.legend.previous_text': 'valore di prima',
    'debug.legend.default': 'grigio',
    'debug.legend.default_text': 'default del chiosco',
    'debug.steps': 'Passi del turno',
    'debug.state': 'Stato dopo il turno',
    'settings.reset': 'Torna alle impostazioni del preset',
    'widget.variant.standard': 'Variante 1',
    'widget.variant.cards': 'Grafico',
    'widget.name.counters': 'Contatori (passeggeri)',
    'widget.name.attract': 'Benvenuto',
    'widget.name.choice-grid': 'Scelte',
    'widget.name.form': 'Modulo',
    'widget.name.keypad': 'Importo',
    'widget.name.message': 'Messaggio',
    'widget.name.summary': 'Riepilogo',
    'widget.name.ticket': 'Biglietto',
  },
  en: {
    'ai.badge': 'AI assistant',
    'chat.placeholder': 'Type a message…',
    'chat.send': 'Send',
    'chat.timeout': 'No answer from the server. Please try again.',
    'chat.send_failed': 'Message not sent: no connection.',
    'chat.turn_failed': 'Something went wrong. Please try again.',
    'chat.vision_failed': 'I could not read the document. Try again or type the details.',
    'chat.from_operator': 'Operator',
    'chat.announcement': 'Notice',
    'chat.not_sent': 'not sent',
    'chat.empty': 'Talk to me or touch the screen.',
    'chat.history': 'The conversation so far',
    'chat.back_to_current': 'Back to the current message',
    'voice.mic_on': 'Microphone on',
    'voice.mic_off': 'Turn the microphone on',
    'voice.stop': 'Stop',
    'voice.listening': 'Listening…',
    'voice.processing': 'Thinking…',
    'voice.speaking': 'Speaking',
    'voice.interrupted': 'Interrupted',
    'voice.disabled_by_mode': 'Voice is off in this mode',
    'voice.error': 'Microphone not available',
    'action.restart': 'Start over',
    'action.help': 'Help',
    'action.help.words': 'Help',
    'action.language': 'Language',
    'language.it': 'Italiano',
    'language.en': 'English',
    'language.de': 'Deutsch',
    'session.copy': 'Copy the session id',
    'session.copied': 'Copied',
    'session.title': 'Session',
    'session.new': 'New session',
    'error.part_failed': 'This part did not load.',
    'error.details': 'Technical details',
    'menu.back': 'Back',
    'menu.return': 'Back',
    'language.fr': 'Français',
    'language.es': 'Español',
    'language.choose': 'Scegli la lingua · Choose your language',
    'status.online': 'Online',
    'status.connecting': 'Connecting…',
    'status.retrying': 'Reconnecting…',
    'status.offline': 'Offline',
    'status.starting': 'Starting…',
    'status.replay': 'Replay',
    'status.recovery': 'If you prefer, touch the screen.',
    'inactivity.title': 'Are you still there?',
    'inactivity.here': "I'm here",
    'inactivity.seconds': '{n} s',
    'mode.title': 'Accessibility',
    'mode.none': 'Standard',
    'mode.visual': 'Low vision',
    'mode.hearing': 'Hearing',
    'mode.motor': 'Reduced mobility',
    'mode.cognitive': 'Simple',
    'mode.local': 'this screen only',
    'preset.kiosk': 'Avatar',
    'preset.text': 'Text',
    'preset.main_bar_top': 'Bar on top',
    'preset.content_first': 'Content on top',
    'role.operator': 'Operator connected to session {id}',
    'role.replay': 'Replay of session {id}',
    'role.replay_short': 'Replay {id}',
    'role.operator_short': 'Operator · {id}',
    'role.replay_tag': 'Replay',
    'role.operator_tag': 'Operator',
    'screen.unknown': 'Screen "{name}" has no look in this interface yet.',
    'widget.unknown': 'Widget "{name}" does not exist.',
    'widget.bad_props': 'Invalid data for widget "{name}".',
    'overlay.close': 'Close',
    'keyboard.space': 'space',
    'keyboard.done': 'Done',
    'boot.connecting': 'Connecting to the kiosk…',
    'boot.no_domain': 'The backend does not say which domain it runs, and no pack recognises the session.',
    'boot.unknown_domain': 'No interface for domain "{domain}".',
    'boot.replay_failed': 'Cannot read the recorded session {id}.',
    'boot.operator_needs_session': 'To join as an operator, add ?session=<id>.',
    'module.progress': 'Progress',
    'module.chat': 'Conversation',
    'module.composer': 'Typing and voice',
    'module.avatar_window': 'Avatar window',
    'module.content': 'Content',
    'module.main_bar': 'Main bar',
    'module.status': 'Status bar',
    'theme.classico': 'Classic',
    'theme.essenziale': 'Essential',
    'preset.essenziale': 'Essential',
    'settings.theme': 'Theme',
    'settings.tab.look': 'Look',
    'settings.tab.parts': 'Parts',
    'settings.tab.screens': 'Screens',
    'settings.screens.title': 'Each step’s widget',
    'settings.screens.none': 'This domain has no screen with more than one look.',
    'settings.screens.end': 'End',
    'settings.details.title': 'Card details',
    'settings.details.closes': 'They close by themselves after',
    'settings.details.seconds': '{n} s',
    'settings.details.never': 'Never',
    'settings.face': 'Assistant',
    'settings.background': 'Background',
    'background.auto': 'Automatic',
    'background.none': 'None',
    'face.auto': 'Automatic',
    'face.musetalk': 'MuseTalk',
    'face.lowpoly': '3D',
    'face.idle': 'Idle',
    'face.particles': 'Particles',
    'settings.scheme': 'Colours',
    'settings.scheme_light': 'Light',
    'settings.scheme_dark': 'Dark',
    'settings.modules': 'Look of the parts',
    'settings.lyrics_history': 'Earlier lines shown',
    'settings.karaoke': 'Light up the words as they are spoken',
    'module.avatar': 'Avatar',
    'module.variant.standard': 'Standard',
    'module.variant.minimal_top': 'Minimal',
    'module.variant.lyrics': 'Lyrics',
    'module.variant.portrait': 'Portrait',
    'module.variant.pills': 'Pills',
    'module.variant.pill_composer': 'Pill',
    'module.variant.minimal_footer': 'Minimal',
    'widget.variant.single': 'One at a time',
    'widget.variant.card': 'Card',
    'widget.variant.classic': 'Variant 2 (classic)',
    'widget.variant.video': 'Video',
    'status.step_of': '{n} of {total} · {name}',
    'settings.title': 'Settings',
    'settings.ui': 'Interface',
    'settings.chat': 'Conversation',
    'settings.chat_align': 'Message layout',
    'settings.align_side': 'On the sides',
    'settings.align_center': 'Centred',
    'settings.chat_size': 'Text size',
    'settings.chat_width': 'Message width',
    'settings.screen': 'Screen',
    'settings.frame_padding': 'Margin from the edges',
    'settings.panel_padding': 'Space inside cards',
    'settings.role_frame': 'Replay and operator frame',
    'settings.frame_solid': 'Solid',
    'settings.frame_fade': 'Faded',
    'settings.frame_off': 'None',
    'settings.none': 'None',
    'settings.widgets': 'Look of the panels',
    'settings.auto': 'Automatic',
    'settings.options': 'Options',
    'settings.touch_pills': 'Show touches in the chat',
    'settings.keyboard_button': 'On-screen keyboard button',
    'settings.keyboard_place': 'Where the keyboard opens',
    'settings.keyboard_place.bottom': 'At the bottom, large',
    'settings.keyboard_place.box': 'Above the box, small',
    'settings.motion': 'Animations',
    'settings.speak_typed': 'Speak with the microphone off too',
    'settings.turn_details': 'Debug under the replies',
    'debug.results': 'on screen',
    'debug.now_default': '(now the default)',
    'debug.changed': 'State after the turn: {n} fields changed',
    'debug.legend.changed': 'green',
    'debug.legend.changed_text': 'changed in this turn',
    'debug.legend.earlier': 'yellow',
    'debug.legend.earlier_text': 'set in earlier turns',
    'debug.legend.previous': 'struck out',
    'debug.legend.previous_text': 'value before',
    'debug.legend.default': 'grey',
    'debug.legend.default_text': 'kiosk default',
    'debug.steps': 'Steps of the turn',
    'debug.state': 'State after the turn',
    'settings.reset': 'Back to the preset settings',
    'widget.variant.standard': 'Variant 1',
    'widget.variant.cards': 'Graphic',
    'widget.name.counters': 'Counters (passengers)',
    'widget.name.attract': 'Welcome',
    'widget.name.choice-grid': 'Choices',
    'widget.name.form': 'Form',
    'widget.name.keypad': 'Amount',
    'widget.name.message': 'Message',
    'widget.name.summary': 'Summary',
    'widget.name.ticket': 'Ticket',
  },
  de: {
    'ai.badge': 'KI-Assistent',
    'chat.placeholder': 'Nachricht schreiben…',
    'chat.send': 'Senden',
    'chat.timeout': 'Keine Antwort vom Server. Bitte erneut versuchen.',
    'chat.send_failed': 'Nachricht nicht gesendet: keine Verbindung.',
    'chat.turn_failed': 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
    'chat.vision_failed': 'Ich konnte das Dokument nicht lesen. Bitte erneut versuchen oder die Daten eingeben.',
    'chat.from_operator': 'Mitarbeiter',
    'chat.announcement': 'Hinweis',
    'chat.not_sent': 'nicht gesendet',
    'chat.empty': 'Sprich mit mir oder berühre den Bildschirm.',
    'chat.history': 'Das bisherige Gespräch',
    'chat.back_to_current': 'Zurück zur aktuellen Nachricht',
    'voice.mic_on': 'Mikrofon an',
    'voice.mic_off': 'Mikrofon einschalten',
    'voice.stop': 'Stopp',
    'voice.listening': 'Ich höre zu…',
    'voice.processing': 'Ich denke nach…',
    'voice.speaking': 'Ich spreche',
    'voice.interrupted': 'Unterbrochen',
    'voice.disabled_by_mode': 'Stimme in diesem Modus aus',
    'voice.error': 'Mikrofon nicht verfügbar',
    'action.restart': 'Neu beginnen',
    'action.help': 'Hilfe',
    'action.help.words': 'Hilfe',
    'action.language': 'Sprache',
    'language.it': 'Italiano',
    'language.en': 'English',
    'language.de': 'Deutsch',
    'session.copy': 'Sitzungs-ID kopieren',
    'session.copied': 'Kopiert',
    'session.title': 'Sitzung',
    'session.new': 'Neue Sitzung',
    'error.part_failed': 'Dieser Teil wurde nicht geladen.',
    'error.details': 'Technische Details',
    'menu.back': 'Zurück',
    'menu.return': 'Zurück',
    'language.fr': 'Français',
    'language.es': 'Español',
    'language.choose': 'Scegli la lingua · Choose your language',
    'status.online': 'Online',
    'status.connecting': 'Verbinden…',
    'status.retrying': 'Neu verbinden…',
    'status.offline': 'Offline',
    'status.starting': 'Start…',
    'status.replay': 'Wiedergabe',
    'status.recovery': 'Wenn du möchtest, berühre den Bildschirm.',
    'inactivity.title': 'Bist du noch da?',
    'inactivity.here': 'Ich bin da',
    'inactivity.seconds': '{n} s',
    'mode.title': 'Barrierefreiheit',
    'mode.none': 'Standard',
    'mode.visual': 'Sehschwäche',
    'mode.hearing': 'Hören',
    'mode.motor': 'Eingeschränkte Mobilität',
    'mode.cognitive': 'Einfach',
    'mode.local': 'nur auf diesem Bildschirm',
    'preset.kiosk': 'Avatar',
    'preset.text': 'Text',
    'preset.main_bar_top': 'Leiste oben',
    'preset.content_first': 'Inhalt oben',
    'role.operator': 'Bediener mit Sitzung {id} verbunden',
    'role.replay': 'Wiedergabe der Sitzung {id}',
    'role.replay_short': 'Wiedergabe {id}',
    'role.operator_short': 'Bediener · {id}',
    'role.replay_tag': 'Wiedergabe',
    'role.operator_tag': 'Bediener',
    'screen.unknown': 'Der Bildschirm "{name}" hat in dieser Oberfläche noch kein Aussehen.',
    'widget.unknown': 'Das Widget "{name}" existiert nicht.',
    'widget.bad_props': 'Ungültige Daten für das Widget "{name}".',
    'overlay.close': 'Schließen',
    'keyboard.space': 'Leertaste',
    'keyboard.done': 'Fertig',
    'boot.connecting': 'Verbindung zum Terminal…',
    'boot.no_domain': 'Das Backend nennt keine Domäne, und kein Paket erkennt die Sitzung.',
    'boot.unknown_domain': 'Keine Oberfläche für die Domäne "{domain}".',
    'boot.replay_failed': 'Die aufgezeichnete Sitzung {id} kann nicht gelesen werden.',
    'boot.operator_needs_session': 'Zum Verbinden als Bediener ?session=<id> angeben.',
    'module.progress': 'Fortschritt',
    'module.chat': 'Gespräch',
    'module.composer': 'Schreiben und Stimme',
    'module.avatar_window': 'Avatar-Fenster',
    'module.content': 'Inhalt',
    'module.main_bar': 'Hauptleiste',
    'module.status': 'Statusleiste',
    'theme.classico': 'Klassisch',
    'theme.essenziale': 'Essenziell',
    'preset.essenziale': 'Essenziell',
    'settings.theme': 'Design',
    'settings.tab.look': 'Aussehen',
    'settings.tab.parts': 'Bausteine',
    'settings.tab.screens': 'Bildschirme',
    'settings.screens.title': 'Das Widget jedes Schritts',
    'settings.screens.none': 'Dieser Bereich hat keinen Bildschirm mit mehreren Ansichten.',
    'settings.screens.end': 'Ende',
    'settings.details.title': 'Kartendetails',
    'settings.details.closes': 'Sie schließen sich von selbst nach',
    'settings.details.seconds': '{n} s',
    'settings.details.never': 'Nie',
    'settings.face': 'Assistent',
    'settings.background': 'Hintergrund',
    'background.auto': 'Automatisch',
    'background.none': 'Keiner',
    'face.auto': 'Automatisch',
    'face.musetalk': 'MuseTalk',
    'face.lowpoly': '3D',
    'face.idle': 'Ruhend',
    'face.particles': 'Partikel',
    'settings.scheme': 'Farben',
    'settings.scheme_light': 'Hell',
    'settings.scheme_dark': 'Dunkel',
    'settings.modules': 'Aussehen der Teile',
    'settings.lyrics_history': 'Sichtbare frühere Sätze',
    'settings.karaoke': 'Wörter beim Sprechen hervorheben',
    'module.avatar': 'Avatar',
    'module.variant.standard': 'Standard',
    'module.variant.minimal_top': 'Minimal',
    'module.variant.lyrics': 'Lyrics',
    'module.variant.portrait': 'Porträt',
    'module.variant.pills': 'Pillen',
    'module.variant.pill_composer': 'Pille',
    'module.variant.minimal_footer': 'Minimal',
    'widget.variant.single': 'Einzeln',
    'widget.variant.card': 'Karte',
    'widget.variant.classic': 'Variante 2 (klassisch)',
    'widget.variant.video': 'Video',
    'status.step_of': '{n} von {total} · {name}',
    'settings.title': 'Einstellungen',
    'settings.ui': 'Oberfläche',
    'settings.chat': 'Gespräch',
    'settings.chat_align': 'Anordnung der Nachrichten',
    'settings.align_side': 'Seitlich',
    'settings.align_center': 'Mittig',
    'settings.chat_size': 'Textgröße',
    'settings.chat_width': 'Breite der Nachrichten',
    'settings.screen': 'Bildschirm',
    'settings.frame_padding': 'Abstand zum Rand',
    'settings.panel_padding': 'Innenabstand der Karten',
    'settings.role_frame': 'Rahmen bei Replay und Operator',
    'settings.frame_solid': 'Voll',
    'settings.frame_fade': 'Verlauf',
    'settings.frame_off': 'Keiner',
    'settings.none': 'Keiner',
    'settings.widgets': 'Aussehen der Felder',
    'settings.auto': 'Automatisch',
    'settings.options': 'Optionen',
    'settings.touch_pills': 'Berührungen im Chat zeigen',
    'settings.keyboard_button': 'Taste der Bildschirmtastatur',
    'settings.keyboard_place': 'Wo die Tastatur erscheint',
    'settings.keyboard_place.bottom': 'Unten, groß',
    'settings.keyboard_place.box': 'Über dem Feld, klein',
    'settings.motion': 'Animationen',
    'settings.speak_typed': 'Auch bei ausgeschaltetem Mikrofon sprechen',
    'settings.turn_details': 'Debug unter den Antworten',
    'debug.results': 'auf dem Bildschirm',
    'debug.now_default': '(jetzt der Standard)',
    'debug.changed': 'Zustand nach dem Schritt: {n} Felder geändert',
    'debug.legend.changed': 'grün',
    'debug.legend.changed_text': 'in diesem Schritt geändert',
    'debug.legend.earlier': 'gelb',
    'debug.legend.earlier_text': 'in früheren Schritten gesetzt',
    'debug.legend.previous': 'durchgestrichen',
    'debug.legend.previous_text': 'vorheriger Wert',
    'debug.legend.default': 'grau',
    'debug.legend.default_text': 'Standard des Kiosks',
    'debug.steps': 'Ablauf des Schritts',
    'debug.state': 'Zustand nach dem Schritt',
    'settings.reset': 'Zurück zu den Preset-Einstellungen',
    'widget.variant.standard': 'Variante 1',
    'widget.variant.cards': 'Grafisch',
    'widget.name.counters': 'Zähler (Reisende)',
    'widget.name.attract': 'Willkommen',
    'widget.name.choice-grid': 'Auswahl',
    'widget.name.form': 'Formular',
    'widget.name.keypad': 'Betrag',
    'widget.name.message': 'Nachricht',
    'widget.name.summary': 'Zusammenfassung',
    'widget.name.ticket': 'Ticket',
  },
};
